// Pannello di configurazione integrazione WhatsApp Business API
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  templateLanguage: z.string().min(2).max(5).default('it'),
  enableReportNotification: z.boolean().default(true),
  enableAppointmentReminder: z.boolean().default(true),
  enableStatusUpdate: z.boolean().default(true),
  enableCheckInConfirmation: z.boolean().default(true),
})

type FormValues = z.infer<typeof schema>

interface WhatsAppConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function WhatsAppConfig({ currentMetadata, onSaved }: WhatsAppConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      templateLanguage: (currentMetadata?.['templateLanguage'] as string) ?? 'it',
      enableReportNotification: (currentMetadata?.['enableReportNotification'] as boolean) ?? true,
      enableAppointmentReminder: (currentMetadata?.['enableAppointmentReminder'] as boolean) ?? true,
      enableStatusUpdate: (currentMetadata?.['enableStatusUpdate'] as boolean) ?? true,
      enableCheckInConfirmation: (currentMetadata?.['enableCheckInConfirmation'] as boolean) ?? true,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/whatsapp/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  const templateDescriptions = [
    { key: 'enableReportNotification', label: 'Notifica rapportino completato', template: 'report_completed' },
    { key: 'enableAppointmentReminder', label: 'Promemoria appuntamento', template: 'appointment_reminder' },
    { key: 'enableStatusUpdate', label: 'Aggiornamento stato intervento', template: 'intervention_status' },
    { key: 'enableCheckInConfirmation', label: 'Conferma check-in tecnico', template: 'checkin_confirmation' },
  ]

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['WHATSAPP_PHONE_ID', 'WHATSAPP_TOKEN']} />

      <div className="rounded-lg border border-[var(--fc-border)] p-3 bg-[var(--fc-surface)]">
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          <span className="font-medium text-[var(--fc-foreground)]">Template pre-approvati richiesti:</span>{' '}
          I template devono essere approvati nel Meta Business Manager prima di essere utilizzati.
          Contattare Meta per richiedere l'approvazione dei template:
          report_completed, appointment_reminder, intervention_status, checkin_confirmation.
        </p>
      </div>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Lingua template messaggi
          </label>
          <Input
            type="text"
            placeholder="it"
            maxLength={5}
            {...register('templateLanguage')}
            error={errors.templateLanguage?.message}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Codice lingua ISO (es. "it", "en", "de"). Deve corrispondere alla lingua dei template approvati.
          </p>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-[var(--fc-foreground)] mb-2">
            Template abilitati
          </legend>
          <div className="space-y-2">
            {templateDescriptions.map(({ key, label, template }) => (
              <div key={key} className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id={key}
                  className="mt-0.5 h-4 w-4 rounded border-[var(--fc-border)]"
                  {...register(key as keyof FormValues)}
                />
                <div>
                  <label htmlFor={key} className="text-sm text-[var(--fc-foreground)]">
                    {label}
                  </label>
                  <p className="text-xs text-[var(--fc-foreground-muted)] font-mono">{template}</p>
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>
            Annulla
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            Salva configurazione
          </Button>
        </div>
      </form>
    </div>
  )
}
